/* Admin-only kill-switch for the Power Dialer.
 * When OFF, the sidebar entry is hidden and every backend route returns 403.
 * Ships as its own tiny card so Tracy can drop it wherever in Admin. */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PhoneCall, Shield, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

export default function PowerDialerAdminToggle() {
  const [enabled, setEnabled] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/power-dialer/admin/platform-flag");
        setEnabled(!!data.enabled);
      } catch {
        setEnabled(false);
      }
    })();
  }, []);

  const toggle = async () => {
    if (enabled === null) return;
    // Confirmation before turning it ON — this is the biggest legal-exposure
    // switch in the app; make sure the admin means to flip it.
    if (!enabled) {
      const ok = window.confirm(
        "Turning ON the Power Dialer platform-wide.\n\n" +
        "Confirm you have:\n" +
        "  • Consulted a telemarketing / real-estate attorney\n" +
        "  • Configured DNC scrubbing procedures\n" +
        "  • Reviewed TCPA + state law obligations\n\n" +
        "Proceed?",
      );
      if (!ok) return;
    }
    setBusy(true);
    try {
      const { data } = await api.patch("/power-dialer/admin/platform-flag", {
        enabled: !enabled,
      });
      setEnabled(!!data.enabled);
      toast.success(`Power Dialer is now ${data.enabled ? "ENABLED" : "DISABLED"} platform-wide.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Toggle failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="card" data-testid="power-dialer-admin-toggle">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0"
             style={{ background: enabled ? "rgba(16, 185, 129, 0.15)" : "rgba(107, 114, 128, 0.15)",
                       color: enabled ? "#10B981" : "#6B7280" }}>
          <PhoneCall size={20} />
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="label-eyebrow flex items-center gap-1.5">
            <Shield size={11} /> Platform kill-switch
          </div>
          <div className="font-display font-bold text-lg mt-1">
            Power Dialer · <span className={enabled ? "text-emerald-600" : "text-stone-500"}>
              {enabled === null ? "…" : enabled ? "ENABLED" : "DISABLED"}
            </span>
          </div>
          <p className="text-[var(--ink-2)] text-sm mt-1">
            When OFF: the sidebar entry is hidden for all users and every backend route
            returns 403 — a clean legal safe-harbor position. Flip ON only after you&apos;ve
            reviewed TCPA / state calling-law obligations.
          </p>
        </div>
        <button type="button" onClick={toggle} disabled={busy || enabled === null}
                className={enabled ? "btn-outline" : "btn-money"}
                data-testid="power-dialer-toggle-button">
          {busy ? <Loader2 size={14} className="animate-spin" /> : null}
          {enabled ? "Turn OFF" : "Turn ON"}
        </button>
      </div>
    </div>
  );
}
